import { useState, useRef } from 'react';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const buttonRef = useRef(null);

  const handleMouseMove = (e) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    }
  };

  return (
    <section id="home" className="w-full relative pt-20 overflow-hidden" style={{ backgroundColor: '#000000' }}>
      {/* Base Grid Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='%23D946EF' stroke-width='0.5' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      {/* Circuit Board Pattern */}
      <div className="absolute inset-0 opacity-15">
        <svg className="w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="circuitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#D946EF', stopOpacity: 0.6 }} />
              <stop offset="50%" style={{ stopColor: '#06B6D4', stopOpacity: 0.4 }} />
              <stop offset="100%" style={{ stopColor: '#D946EF', stopOpacity: 0.6 }} />
            </linearGradient>
          </defs>
          {/* Circuit lines */}
          <path d="M 50 200 L 200 200 L 200 150 L 350 150 L 350 250 L 500 250" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 800 100 L 950 100 L 950 200 L 1100 200 L 1100 300" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 100 500 L 300 500 L 300 400 L 500 400 L 500 600 L 700 600" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" opacity="0.5" />
          <path d="M 900 450 L 1100 450 L 1100 550 L 1000 550 L 1000 650" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" opacity="0.5" />
          {/* Circuit nodes */}
          <circle cx="200" cy="200" r="8" fill="#D946EF" opacity="0.7" />
          <circle cx="500" cy="250" r="8" fill="#06B6D4" opacity="0.7" />
          <circle cx="950" cy="100" r="8" fill="#D946EF" opacity="0.7" />
          <circle cx="300" cy="500" r="8" fill="#06B6D4" opacity="0.7" />
          <circle cx="1100" cy="450" r="8" fill="#D946EF" opacity="0.7" />
        </svg>
      </div>

      {/* Hexagon Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='104' viewBox='0 0 120 104' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M60 0L120 30V74L60 104L0 74V30Z' fill='none' stroke='%23D946EF' stroke-width='1' opacity='0.3'/%3E%3C/svg%3E")`,
          backgroundSize: '120px 104px',
          backgroundPosition: '0 0'
        }}></div>
      </div>

      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Radial Gradient Overlay */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,1) 100%)'
      }}></div>
      
      {/* Main Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-transparent via-pink-900/20 to-cyan-900/30"></div>
      
      {/* Shimmer Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 animate-shimmer"></div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
        <div className="text-center">

          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
            We Build Modern &<br />
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent animate-gradient">
              Professional Websites
            </span><br />
            for Your Business
          </h2>
          <p className="text-xl mb-10 max-w-3xl mx-auto leading-relaxed drop-shadow-lg" style={{ color: '#EEEEEE' }}>
            Fast delivery, clean design and perfectly mobile responsive.<br />
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button className="group px-8 py-4 rounded-full text-white font-semibold text-lg shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 relative overflow-hidden" style={{ backgroundColor: '#D946EF' }}>
              <span className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              <span className="relative flex items-center gap-2">
                Get a Free Quote
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </span>
            </button>
            <button 
              ref={buttonRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setMousePosition({ x: 50, y: 50 })}
              className="group px-8 py-4 rounded-full text-white font-semibold text-lg backdrop-blur-sm border-2 border-white/20 hover:border-transparent transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1 relative overflow-hidden bg-transparent"
            >
              <span 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{
                  background: `radial-gradient(circle 150px at ${mousePosition.x}% ${mousePosition.y}%, #D946EF 0%, rgba(217, 70, 239, 0.6) 40%, transparent 70%)`,
                }}
              ></span>
              <span className="relative z-10">View Our Work</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

